import qs from 'qs'
import { roleuser } from '@edulastic/constants'
import {
  reportGroupType,
  reportNavType,
  sharedReportDataVisibility as DATA_VISIBILITY,
} from '@edulastic/constants/const/report'

import navigation from '../../../common/static/json/navigation.json'
import { DGA_VISIBLE_REPORTS } from '../../../common/constants/dataWarehouseReports'
import { fromSharedFilters } from './adapters'

/**
 * Open a shared report as the normal report, prefilled with the share's
 * filters resolved for the viewer (DAT-649).
 */

/** Shares without a data visibility run as "Same data as me". */
export const resolveDataVisibility = (sharedReport) =>
  sharedReport?.sharedWith?.dataVisibility || DATA_VISIBILITY.SAME_AS_ME

/**
 * History state key set on a navigation that (re)opens a report from a share.
 * The report routes are keyed by it so they remount in the same render as the
 * navigation and each filter component re-runs its first-load init once.
 */
export const SHARED_REPORT_OPEN_STATE_KEY = 'sharedReportOpenId'

export const newSharedReportOpenState = () => ({
  [SHARED_REPORT_OPEN_STATE_KEY]: `${Date.now()}-${Math.random()}`,
})

/**
 * Report types that can open as the normal report (HLD §4.1), keyed to the
 * `reportGroupType` whose filters they use. The SEL reports sit in the survey
 * group but use the SAR / SMR filters.
 */
export const REPORT_TYPE_FAMILY = {
  [reportNavType.ASSESSMENT_SUMMARY]: reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.PEER_PERFORMANCE]: reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.QUESTION_ANALYSIS]: reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.RESPONSE_FREQUENCY]: reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.PERFORMANCE_BY_STANDARDS]:
    reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.PERFORMANCE_BY_STUDENTS]:
    reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.DW_SEL_RESPONSE_SUMMARY_REPORT]:
    reportGroupType.SINGLE_ASSESSMENT_REPORT,
  [reportNavType.PERFORMANCE_OVER_TIME]:
    reportGroupType.MULTIPLE_ASSESSMENT_REPORT,
  [reportNavType.PEER_PROGRESS_ANALYSIS]:
    reportGroupType.MULTIPLE_ASSESSMENT_REPORT,
  [reportNavType.STUDENT_PROGRESS]: reportGroupType.MULTIPLE_ASSESSMENT_REPORT,
  [reportNavType.PRE_VS_POST]: reportGroupType.MULTIPLE_ASSESSMENT_REPORT,
  [reportNavType.COMPLETION_REPORT]: reportGroupType.MULTIPLE_ASSESSMENT_REPORT,
  [reportNavType.STANDARDS_PERFORMANCE_SUMMARY]:
    reportGroupType.STANDARDS_MASTERY_REPORT,
  [reportNavType.STANDARDS_GRADEBOOK]: reportGroupType.STANDARDS_MASTERY_REPORT,
  [reportNavType.STANDARDS_PROGRESS]: reportGroupType.STANDARDS_MASTERY_REPORT,
  [reportNavType.DW_SEL_REPORT]: reportGroupType.STANDARDS_MASTERY_REPORT,
  [reportNavType.STUDENT_PROFILE_SUMMARY]:
    reportGroupType.STUDENT_PROFILE_REPORT,
  [reportNavType.STUDENT_ASSESSMENT_PROFILE]:
    reportGroupType.STUDENT_PROFILE_REPORT,
  [reportNavType.STUDENT_PROGRESS_PROFILE]:
    reportGroupType.STUDENT_PROFILE_REPORT,
  [reportNavType.STUDENT_MASTERY_PROFILE]:
    reportGroupType.STUDENT_PROFILE_REPORT,
  [reportNavType.MULTIPLE_ASSESSMENT_REPORT_DW]:
    reportGroupType.MULTIPLE_ASSESSMENT_REPORT_DW,
  [reportNavType.WHOLE_LEARNER_REPORT]: reportGroupType.WHOLE_LEARNER_REPORT,
  [reportNavType.DW_EFFICACY_REPORT]: reportGroupType.DW_EFFICACY_REPORT,
  [reportNavType.DW_DOMAIN_ANALYSIS_REPORT]:
    reportGroupType.DW_DOMAIN_ANALYSIS_REPORT,
}

const DATA_STUDIO_FAMILIES = [
  reportGroupType.MULTIPLE_ASSESSMENT_REPORT_DW,
  reportGroupType.WHOLE_LEARNER_REPORT,
  reportGroupType.DW_EFFICACY_REPORT,
  reportGroupType.DW_DOMAIN_ANALYSIS_REPORT,
]

const SURVEY_REPORT_TYPES = [
  reportNavType.DW_SEL_REPORT,
  reportNavType.DW_SEL_RESPONSE_SUMMARY_REPORT,
]

const STUDENT_ROUTE_FAMILIES = [
  reportGroupType.STUDENT_PROFILE_REPORT,
  reportGroupType.WHOLE_LEARNER_REPORT,
]

/**
 * Whether the viewer can open `reportType` as a normal report. When not, the
 * share keeps today's read-only shared view.
 */
export const canOpenReportType = (reportType, { role, features = {} }) => {
  const family = REPORT_TYPE_FAMILY[reportType]
  if (!family) {
    return false
  }
  if (role === roleuser.DISTRICT_GROUP_ADMIN) {
    return DGA_VISIBLE_REPORTS.includes(reportType)
  }
  if (SURVEY_REPORT_TYPES.includes(reportType)) {
    return !!features.surveyReports
  }
  if (DATA_STUDIO_FAMILIES.includes(family)) {
    return !!features.dataWarehouseReports
  }
  return true
}

/**
 * Whether the sharer let viewers edit the shared filters. Shares saved before
 * the permission existed are locked and keep the read-only shared view.
 */
export const canEditFilters = (sharedReport) =>
  sharedReport?.allowFilterEdit === true

/**
 * "Their data" shares that allow editing have no shared view: they open as
 * the normal report.
 */
export const opensAsNormalReport = (sharedReport, { role, features }) =>
  !!sharedReport &&
  canEditFilters(sharedReport) &&
  canOpenReportType(sharedReport.reportType, { role, features }) &&
  resolveDataVisibility(sharedReport) === DATA_VISIBILITY.THEIR_DATA

/**
 * Whether any saved filter was not applied for the viewer: no access, or not
 * applicable to their role. Remapped terms were applied, so they don't count.
 */
export const hasDroppedFilters = (resolved) =>
  !!resolved?.blocked ||
  (resolved?.removed || []).some(({ reason }) => reason !== 'remapped')

/**
 * Normal report URL for a resolved share. Never carries `reportId`, which
 * would switch the page into the read-only shared view.
 */
export const buildNormalReportUrl = (
  resolved,
  { defaultTermId, dsDefaultFilters = {} } = {}
) => {
  const { reportType, route = {} } = resolved
  const family = REPORT_TYPE_FAMILY[reportType]
  const query = fromSharedFilters(family, reportType, resolved.filters || {})
  // Keys the share didn't save use the viewer's defaults (HLD §4.4). A
  // non-empty URL skips each report's first-load defaults, so add the Data
  // Studio defaults here, and always a term so the report loads by itself.
  if (DATA_STUDIO_FAMILIES.includes(family)) {
    Object.entries(
      fromSharedFilters(family, reportType, dsDefaultFilters)
    ).forEach(([key, value]) => {
      if (query[key] === undefined) {
        query[key] = value
      }
    })
  }
  if (!query.termId && defaultTermId) {
    query.termId = defaultTermId
  }
  let path = `/author/reports/${reportType}`
  // the test/student segment is part of the route even without an id; the
  // report then lets the viewer pick one
  if (family === reportGroupType.SINGLE_ASSESSMENT_REPORT) {
    path += `/test/${route.testId || ''}`
  } else if (STUDENT_ROUTE_FAMILIES.includes(family)) {
    path += `/student/${route.studentId || ''}`
  }
  const search = qs.stringify(query, { arrayFormat: 'comma', encode: true })
  return search ? `${path}?${search}` : path
}

/** Fits a share's title in banner copy on small screens; full title on hover. */
export const REPORT_TITLE_MAX_LENGTH = 35

export const truncateReportTitle = (
  title = '',
  maxLength = REPORT_TITLE_MAX_LENGTH
) => {
  const text = `${title || ''}`.trim()
  return text.length > maxLength
    ? `${text.slice(0, maxLength).trimEnd()}…`
    : text
}

/**
 * `reportGroupType` of a report type, from its navigation group title:
 * 'Single Assessment Report' => 'single-assessment-report'.
 */
export const getReportGroupType = (reportType) =>
  (navigation.locToData[reportType]?.groupTitle || '-')
    .toLowerCase()
    .split(' ')
    .join('-')

/** Read-only shared view URL of a share; null for a report type without one. */
export const sharedReportViewUrl = ({ _id, reportType, filters = {} }) => {
  const path = `/author/reports/${reportType}`
  switch (reportType) {
    case reportNavType.WHOLE_LEARNER_REPORT:
      return `${path}/student/${filters.studentId}?termId=${filters.termId}&reportId=${_id}`
    case reportNavType.MULTIPLE_ASSESSMENT_REPORT_DW:
    case reportNavType.DW_DOMAIN_ANALYSIS_REPORT:
    case reportNavType.DW_EFFICACY_REPORT:
    case reportNavType.DW_SEL_REPORT:
      return `${path}?termId=${filters.termId}&reportId=${_id}`
    case reportNavType.DW_SEL_RESPONSE_SUMMARY_REPORT:
      return `${path}/test/${filters.testId}?termId=${filters.termId}&reportId=${_id}`
    default:
  }
  switch (getReportGroupType(reportType)) {
    case reportGroupType.SINGLE_ASSESSMENT_REPORT:
      return `${path}/test/${filters.testId}?reportId=${_id}`
    case reportGroupType.MULTIPLE_ASSESSMENT_REPORT:
    case reportGroupType.STANDARDS_MASTERY_REPORT:
    case reportGroupType.ENGAGEMENT_REPORT:
      return `${path}?reportId=${_id}`
    case reportGroupType.STUDENT_PROFILE_REPORT:
      return `${path}/student/${filters.studentId}?termId=${filters.termId}&reportId=${_id}`
    default:
      return null
  }
}

// -----| Source of the open normal report, for the banner |----- //
// Kept in sessionStorage so it survives refresh and back/forward without a
// URL param every filter component would have to preserve.

const SOURCE_STORAGE_KEY = 'sharedReportSource'

export const writeSharedReportSource = (source) => {
  try {
    window.sessionStorage.setItem(SOURCE_STORAGE_KEY, JSON.stringify(source))
  } catch (e) {
    // storage unavailable: the banner is skipped, the report still works
  }
}

export const readSharedReportSource = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(SOURCE_STORAGE_KEY))
  } catch (e) {
    return null
  }
}

export const clearSharedReportSource = () => {
  try {
    window.sessionStorage.removeItem(SOURCE_STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}
